import { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import {
  createAvailabilityRecords,
  deleteAvailabilityRecord,
  listAvailability,
} from './services/availabilityService';
import { exportAvailability } from './utils/exportAvailability';
import './App.css';

const {
  FiCalendar,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiDownload,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiSend,
  FiShield,
  FiTrash2,
  FiUser,
} = FiIcons;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const days = [];

  for (let index = 0; index < firstDay; index += 1) {
    days.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const date = new Date(year, month, day);
    days.push({
      day,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
    });
  }

  return days;
}

function CalendarGrid({ year, month, selectedDays, namesByDate, onToggle, onRemove, isDeleting }) {
  const days = useMemo(() => getMonthDays(year, month), [year, month]);

  return (
    <div className="calendar-shell">
      <div className="weekday-row">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {days.map((item, index) => {
          if (!item) {
            return <span className="calendar-empty" key={`empty-${index}`} />;
          }

          const records = namesByDate[item.date] || [];
          const isSelected = selectedDays.includes(item.date);

          return (
            <label className={`calendar-day ${isSelected ? 'selected' : ''}`} key={item.date}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggle(item.date)}
              />
              <span className="day-number">{item.day}</span>
              <span className="day-label">
                {item.isWeekend ? 'Afternoon' : 'Evening / night'}
              </span>
              {records.length > 0 && (
                <span className="volunteer-names">
                  {records.slice(0, 2).map((record) => (
                    <button
                      className="volunteer-name"
                      type="button"
                      key={record.id}
                      title={`Remove ${record.name} from this date`}
                      aria-label={`Remove ${record.name} from ${item.date}`}
                      disabled={isDeleting}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemove(record);
                      }}
                    >
                      {record.name}
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  ))}
                  {records.length > 2 && (
                    <span className="volunteer-more">+{records.length - 2} more</span>
                  )}
                </span>
              )}
              {isSelected && <SafeIcon icon={FiCheck} className="day-check" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ContactField({ icon, label, name, type = 'text', value, onChange, required }) {
  return (
    <label className="contact-field">
      <span className="field-label">
        <SafeIcon icon={icon} />
        {label}
      </span>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={`Enter your ${label.toLowerCase()}`}
      />
    </label>
  );
}

function App() {
  const currentDate = new Date();
  const [calendarDate, setCalendarDate] = useState(
    new Date(currentDate.getFullYear(), 9, 1),
  );
  const [selectedDays, setSelectedDays] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('loading');
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();

  const loadAvailability = async () => {
    try {
      const records = await listAvailability();
      setAvailability(records);
      setStatus('idle');
    } catch {
      setStatus('load-error');
    }
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  const namesByDate = useMemo(
    () => availability.reduce((result, record) => {
      if (!record.date || !record.name) return result;
      const records = result[record.date] || [];
      result[record.date] = records.some((item) => item.id === record.id)
        ? records
        : [...records, record];
      return result;
    }, {}),
    [availability],
  );

  const toggleDay = (date) => {
    setSelectedDays((current) => (
      current.includes(date)
        ? current.filter((item) => item !== date)
        : [...current, date].sort()
    ));
  };

  const updateContact = (event) => {
    setContact((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const changeMonth = (amount) => {
    setCalendarDate((current) => {
      const nextMonth = new Date(current);
      nextMonth.setDate(1);
      nextMonth.setMonth(nextMonth.getMonth() + amount);
      return nextMonth;
    });
  };

  const removeAvailability = async (record) => {
    const confirmed = window.confirm(
      `Remove ${record.name}'s availability from ${record.date}? Anyone can remove an entry.`,
    );
    if (!confirmed) return;

    setStatus('deleting');
    try {
      await deleteAvailabilityRecord(record.id);
      setAvailability((current) => current.filter((item) => item.id !== record.id));
      setStatus('deleted');
    } catch {
      setStatus('error');
    }
  };

  const submitAvailability = async (event) => {
    event.preventDefault();
    if (!selectedDays.length) {
      setStatus('missing-days');
      return;
    }

    setStatus('sending');
    try {
      const records = selectedDays.map((date) => {
        const day = new Date(`${date}T12:00:00`);
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        return {
          ...contact,
          date,
          year: day.getFullYear(),
          month: MONTH_NAMES[day.getMonth()],
          shift: isWeekend ? 'Afternoon' : 'Evening / night',
        };
      });

      const savedRecords = await createAvailabilityRecords(records);
      setAvailability((current) => [...current, ...savedRecords]);
      setStatus('success');
      setSelectedDays([]);
      setContact({ name: '', email: '', phone: '' });
    } catch {
      setStatus('error');
    }
  };

  const message = {
    'load-error': 'Availability could not be loaded. Try refreshing the calendar.',
    'missing-days': 'Please select at least one date before sending.',
    sending: 'Saving your availability...',
    deleting: 'Removing the selected availability...',
    deleted: 'The availability entry was removed.',
    success: 'Thanks! Your availability is now shown on the calendar.',
    error: 'We could not complete that change. Please try again.',
  }[status];
  const isLoading = status === 'loading';

  return (
    <main className="page">
      <section className="hero">
        <div className="brand-mark">
          <SafeIcon icon={FiShield} />
        </div>
        <p className="eyebrow">Baseball Association · Jersey Return</p>
        <h1>Help us get every jersey home.</h1>
        <p className="hero-copy">
          Choose the evenings or weekend afternoons when you can help with jersey returns.
          Volunteer names appear on the shared calendar so everyone can coordinate.
        </p>
      </section>

      <form className="volunteer-card" onSubmit={submitAvailability}>
        <div className="card-section">
          <div className="section-heading">
            <div className="section-icon">
              <SafeIcon icon={FiUser} />
            </div>
            <div>
              <h2>Your details</h2>
              <p>We will use your name for the shared calendar.</p>
            </div>
          </div>
          <div className="contact-grid">
            <ContactField
              icon={FiUser}
              label="Full name"
              name="name"
              value={contact.name}
              onChange={updateContact}
              required
            />
            <ContactField
              icon={FiMail}
              label="Email address"
              name="email"
              type="email"
              value={contact.email}
              onChange={updateContact}
              required
            />
            <ContactField
              icon={FiPhone}
              label="Phone number"
              name="phone"
              type="tel"
              value={contact.phone}
              onChange={updateContact}
              required
            />
          </div>
        </div>

        <div className="card-section calendar-section">
          <div className="calendar-title-row">
            <div className="section-heading">
              <div className="section-icon">
                <SafeIcon icon={FiCalendar} />
              </div>
              <div>
                <h2>When can you help?</h2>
                <p>Select every date that works for you.</p>
              </div>
            </div>
            <div className="month-switcher">
              <button type="button" onClick={() => changeMonth(-1)} aria-label="Previous month">
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <strong>{MONTH_NAMES[month]} {year}</strong>
              <button type="button" onClick={() => changeMonth(1)} aria-label="Next month">
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>

          <div className="availability-note">
            <SafeIcon icon={FiClock} />
            <span>
              <b>Weeknights</b> are evening / night shifts. <b>Weekends</b> are afternoon shifts.
              Click the trash icon beside a name to remove that entry. No email verification is
              required, so anyone can remove a selection.
            </span>
          </div>

          {isLoading ? (
            <div className="calendar-loading">
              <SafeIcon icon={FiRefreshCw} />
              Loading shared availability...
            </div>
          ) : (
            <CalendarGrid
              year={year}
              month={month}
              selectedDays={selectedDays}
              namesByDate={namesByDate}
              onToggle={toggleDay}
              onRemove={removeAvailability}
              isDeleting={status === 'deleting'}
            />
          )}

          <div className="selection-summary">
            <span>
              {selectedDays.length} {selectedDays.length === 1 ? 'date' : 'dates'} selected
            </span>
            {selectedDays.length > 0 && (
              <span className="selected-pill">
                <SafeIcon icon={FiCheck} />
                Ready to submit
              </span>
            )}
          </div>
        </div>

        <div className="card-footer">
          <div className="privacy-copy">
            <SafeIcon icon={FiShield} />
            <span>Names are visible to everyone using this shared calendar.</span>
          </div>
          <div className="footer-actions">
            <button
              className="cancel-button"
              type="button"
              onClick={() => exportAvailability(availability)}
              disabled={isLoading || availability.length === 0}
            >
              <SafeIcon icon={FiDownload} />
              Export roster
            </button>
            <button
              className="submit-button"
              type="submit"
              disabled={isLoading || status === 'sending' || status === 'deleting'}
            >
              <SafeIcon icon={FiSend} />
              {status === 'sending' ? 'Saving...' : 'Send my availability'}
            </button>
          </div>
        </div>

        {message && (
          <p className={`form-status ${['success', 'deleted'].includes(status) ? 'success' : ['sending', 'deleting'].includes(status) ? 'sending' : 'error'}`}>
            {message}
          </p>
        )}
      </form>

      <footer className="page-footer">
        Thank you for supporting our players and families.
      </footer>
    </main>
  );
}

export default App;